/** Shared setup is appended to the author-written instructions, never saved in them. */
export const RUN_SETUP_PROMPT = "**CLI setup:** Run `which method`. If it is missing, follow the [setup instructions](https://withmethod.ai/docs/quickstart.md). Follow any browser sign-in instructions.";

export const DEFAULT_RUN_INSTRUCTIONS = "Run this Method. Read its input definitions, use the available inputs, and ask me for any required values you cannot find. When it finishes, show the result.";

/** The run prompt from the dashboard's Run button: the Method's own run prompt, with the person's note when there is one. */
export function runWithNotePrompt(workflowId: string, versionId: string | null, server: string, authoredPrompt: string | undefined, note: string,
  name = "Method", versionNumber?: number | null) {
  const instructions = authoredPrompt?.trim() || DEFAULT_RUN_INSTRUCTIONS;
  return runAgentPrompt(workflowId, versionId, server, note.trim() ? `${instructions}\n\n**My note for this run:** ${note.trim()}` : instructions, name, versionNumber);
}

/** For a person who joined a workspace: set up Method on this computer, then run the workspace's Methods by name. */
export function teammateSetupPrompt(workspace: string) {
  return [
    `Set up Method on this computer. I joined the workspace ${workspace}, and I want to run its Methods.`,
    "",
    RUN_SETUP_PROMPT,
    "",
    "Then run `method list` and show me the names of the Methods. When I ask you to run one by its name, run `method run 'NAME'`: it always runs the newest version.",
  ].join("\n");
}

/** Run a saved Method again with the inputs of one of its runs. Only what the person names changes. */
export function runAgainPrompt(workflowId: string, versionId: string | null, server: string, run: { id: string; device: boolean }, change: string,
  name = "Method", versionNumber?: number | null) {
  const link = new URL(`/runs/${encodeURIComponent(run.id)}`, server).href;
  const where = run.device ? " They are only in that run's folder on this computer; method finds it." : "";
  const instructions = [`Run this Method again with the same inputs as the run ${link}: add \`--inputs-from ${run.id}\` to the method run command.${where}`,
    change.trim() ? `Change only this: ${change.trim()}` : "Change nothing.",
    "When it finishes, show the result."].join("\n\n");
  return runAgentPrompt(workflowId, versionId, server, instructions, name, versionNumber);
}

/**
 * For a Method that keeps run content on this computer: the person's coding agent records the correction there with
 * method judge, which keeps the words on the computer and fixes the Method there.
 */
export function judgeOnDevicePrompt(workflowId: string, server: string, note: string, name = "Method", runId?: string) {
  const url = new URL(`/methods/${encodeURIComponent(workflowId)}`, server);
  const quote = (value: string) => "'" + value.replaceAll("'", "'\\''") + "'";
  return [
    `Fix this Method on this computer from my correction. Its run content stays here, so the fix runs here too.`,
    "",
    `**Method:** [${name.replace(/[\\`*_[\]]/g, "\\$&")}](${url.href}) (ID \`${workflowId}\`)`,
    ...(runId ? [`**Run that went wrong:** ${new URL(`/runs/${encodeURIComponent(runId)}`, server).href}`] : []),
    `**What is wrong, in my words:** ${note.trim()}`,
    "",
    RUN_SETUP_PROMPT,
    "",
    `In the folder of the .method file with this ID, run the command below. If no file on this computer has this ID, first get it in a new folder: \`method get ${workflowId} --out method.method\`.`,
    "",
    "```sh",
    `method judge ${runId ? quote(runId) : "RUN_ID"} wrong --note ${quote(note.trim())}`,
    "```",
    "",
    "Show me the line it prints: what it changed, or why it changed nothing.",
  ].join("\n");
}

/** versionId: the version the person chose to run. Null runs the newest version, also when a teammate saves a newer one. */
export function runAgentPrompt(
  workflowId: string,
  versionId: string | null,
  server: string,
  authoredPrompt?: string,
  name = "Method",
  versionNumber?: number | null,
) {
  const url = new URL(`/methods/${encodeURIComponent(workflowId)}`, server);
  if (versionId) url.searchParams.set("version", versionId);
  const instructions = authoredPrompt?.trim() || DEFAULT_RUN_INSTRUCTIONS;
  const title = name.replace(/[\\`*_[\]]/g, "\\$&").replace(/\s+/g, " ");
  const label = versionNumber == null || !versionId ? title : `${title} · Version ${versionNumber}`;
  const quote = (value: string) => "'" + value.replaceAll("'", "'\\''") + "'";
  const target = `${quote(workflowId)}${versionId ? ` --version ${quote(versionId)}` : ""} --server ${quote(url.origin)}`;
  return `${instructions}\n\n**Method:** [${label}](${url.href})\n\n${RUN_SETUP_PROMPT}\n\nRun ${versionId ? "this saved version" : "its newest version"}. Method retrieves its files and prepares its dependencies. As soon as the CLI returns a dashboard run link, share it with me so I can monitor the run, including while it waits for inputs or sign-in. Handle any returned request for inputs or sign-in, then continue the same run:\n\n\`\`\`sh\nmethod run ${target}\n\`\`\``;
}

/** The prompt that hands a correction of selected Method or run text to the person's coding agent. */
export function correctionAgentPrompt(selection: {
  workflowId: string;
  versionId: string;
  server: string;
  name?: string;
  versionNumber?: number | null;
  runId?: string;
  stepId?: string;
  stepName?: string;
  quote: string;
}) {
  const url = new URL(`/methods/${encodeURIComponent(selection.workflowId)}`, selection.server);
  url.searchParams.set("version", selection.versionId);
  const title = (selection.name ?? "Method").replace(/[\\`*_[\]]/g, "\\$&").replace(/\s+/g, " ");
  const label = selection.versionNumber == null ? title : `${title} · Version ${selection.versionNumber}`;
  const lines = [
    "Correct this Method. Run `method authoring` first and follow its guidance for corrections: record a case for the problem, change the Method, and check it with `method test` before you save.",
    "",
    `**Method:** [${label}](${url.href})`,
    ...(selection.runId ? [`**Run:** ${new URL(`/runs/${encodeURIComponent(selection.runId)}`, selection.server).href}`] : []),
    ...(selection.stepId ? [`**Step:** ${selection.stepName ?? selection.stepId} (\`${selection.stepId}\`)`] : []),
    "**Selected text:**",
    "",
    ...selection.quote.trim().split("\n").map((line) => `> ${line}`),
    "",
    RUN_SETUP_PROMPT,
    "",
    "**What is wrong:** ",
  ];
  return lines.join("\n");
}

/** The prompt that asks the person's coding agent to connect their app to a saved Method. */
export function connectAgentPrompt(workflowId: string, server: string, name = "Method") {
  const url = new URL(`/methods/${encodeURIComponent(workflowId)}`, server);
  const title = name.replace(/[\\`*_[\]]/g, "\\$&").replace(/\s+/g, " ");
  const quote = (value: string) => "'" + value.replaceAll("'", "'\\''") + "'";
  return [
    "Connect my app to this Method, so that my app runs it in production.",
    "",
    `**Method:** [${title}](${url.href})`,
    "",
    RUN_SETUP_PROMPT,
    "",
    `1. In a new folder, get the Method file: \`method get ${quote(workflowId)} --out method.method --server ${quote(url.origin)}\`.`,
    "2. In that folder, run `method connect APP_FOLDER`, with my app's folder as APP_FOLDER. Ask me if you cannot tell which folder is my app.",
    "3. Add the code that the command prints to my app, tell me which secrets to set where my app runs, and show me one test run.",
  ].join("\n");
}
